import type { AppLocale, CollectionPageJsonLdParams, SeoHreflangLink } from '~/types/UseSeo'
import { stripLocalePrefix } from '~/utils/router'

const DEFAULT_SITE_URL: string = 'https://www.prepeers.co'
const SCHEMA_CONTEXT: string = 'https://schema.org'
const HREFLANG_BY_LOCALE: Record<AppLocale, string> = {
  fr: 'fr-FR',
  en: 'en-US',
  es: 'es-ES',
}
const APP_LOCALES: readonly AppLocale[] = ['fr', 'en', 'es'] as const

/**
 * Normalise l’URL publique du site (sans slash final).
 * @param {string | undefined} raw - Valeur issue de la config runtime.
 * @returns {string} URL de base sans slash terminal.
 */
export function resolveSiteUrl(raw: string | undefined): string {
  return String(raw ?? DEFAULT_SITE_URL).replace(/\/$/, '')
}

/**
 * Retire le slash final superflu d’un chemin (sauf la racine).
 * @param {string} path - Chemin interne commençant par `/`.
 * @returns {string} Chemin normalisé pour canonical.
 */
export function normalizePathForCanonical(path: string): string {
  if (path.endsWith('/') && path.length > 1) return path.slice(0, -1)
  return path
}

/**
 * Construit une URL absolue à partir du site et d’un chemin relatif.
 * @param {string} siteUrl - URL de base du site.
 * @param {string} path - Chemin relatif ou URL absolue déjà complète.
 * @returns {string} URL absolue.
 */
export function absoluteUrl(siteUrl: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  const base: string = resolveSiteUrl(siteUrl)
  const normalized: string = path.startsWith('/') ? path : `/${path}`
  return `${base}${normalized}`
}

/**
 * Construit l’URL canonique d’une page.
 * @param {string} siteUrl - URL de base du site.
 * @param {string} path - Chemin courant de la route.
 * @returns {string} URL canonique complète.
 */
export function buildCanonicalUrl(siteUrl: string, path: string): string {
  return `${resolveSiteUrl(siteUrl)}${normalizePathForCanonical(path)}`
}

/**
 * Chemin localisé selon la stratégie i18n `prefix_except_default`.
 * @param {string} localeCode - Code locale (`fr`, `en`, `es`).
 * @param {string} path - Chemin courant.
 * @returns {string} Chemin avec préfixe locale si nécessaire.
 */
export function pathForLocale(localeCode: string, path: string): string {
  const base: string = stripLocalePrefix(path)
  if (localeCode === 'fr') return base
  if (base === '/') return `/${localeCode}`
  return `/${localeCode}${base}`
}

/**
 * Déduit la locale à partir du chemin URL.
 * @param {string} path - Chemin interne.
 * @returns {AppLocale} Code locale applicatif.
 */
export function localeFromPath(path: string): AppLocale {
  if (path === '/en' || path.startsWith('/en/')) return 'en'
  if (path === '/es' || path.startsWith('/es/')) return 'es'
  return 'fr'
}

/**
 * Valeur `og:locale` (ex. `fr_FR`) d'une locale applicative.
 * @param {AppLocale} locale - Locale de la page courante.
 * @returns {string} Locale au format Open Graph.
 */
export function ogLocaleFor(locale: AppLocale): string {
  return HREFLANG_BY_LOCALE[locale].replace('-', '_')
}

/**
 * Génère les liens hreflang : fr + x-default, et en/es seulement si indexables.
 * @param {string} siteUrl - URL de base du site.
 * @param {string} path - Chemin courant.
 * @param {boolean} langIndexable - en/es incluses dans le cluster si `true`.
 * @returns {SeoHreflangLink[]} Liens `rel=alternate` pour le head.
 */
export function buildHreflangLinks(
  siteUrl: string,
  path: string,
  langIndexable: boolean,
): SeoHreflangLink[] {
  const base: string = resolveSiteUrl(siteUrl)
  const locales: readonly AppLocale[] = langIndexable ? APP_LOCALES : ['fr']
  const links: SeoHreflangLink[] = locales.map((code: AppLocale) => ({
    rel: 'alternate',
    hreflang: HREFLANG_BY_LOCALE[code],
    href: `${base}${pathForLocale(code, path)}`,
  }))
  links.push({
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${base}${pathForLocale('fr', path)}`,
  })
  return links
}

/**
 * Détermine la directive robots à injecter dans le head.
 * @param {boolean} indexable - `NUXT_PUBLIC_SEO_INDEXABLE`.
 * @param {AppLocale} locale - Locale de la page courante.
 * @param {boolean} langIndexable - `NUXT_PUBLIC_LANG_SEO_INDEXABLE` (indexation en/es).
 * @returns {(string | null)} Contenu meta robots ou `null` si indexation autorisée.
 */
export function resolveRobotsMeta(
  indexable: boolean,
  locale: AppLocale,
  langIndexable: boolean,
): string | null {
  if (!indexable) return 'noindex, nofollow'
  // en/es désindexées tant que non traduites, liens toujours suivis
  if (locale !== 'fr' && !langIndexable) return 'noindex, follow'
  return null
}

/**
 * Schéma JSON-LD CollectionPage pour les hubs SEO.
 * @param {CollectionPageJsonLdParams} params - Données de la page.
 * @returns {(Record<string, unknown> | null)} Objet schema.org ou `null` si incomplet.
 */
export function collectionPageJsonLd(
  params: CollectionPageJsonLdParams,
): Record<string, unknown> | null {
  if (!params.name) return null
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'CollectionPage',
    name: params.name,
    description: params.description,
    url: params.url,
  }
}
